import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Users,
  MousePointer,
  Settings,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Save,
  Send,
  PenTool,
  AlertCircle,
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import WizardStepIndicator from "../intake/wizard/WizardStepIndicator";
import DocumentsStep from "./wizard/DocumentsStep";
import SignersStep from "./wizard/SignersStep";
import PlaceFieldsStep from "./wizard/PlaceFieldsStep";
import OptionsStep from "./wizard/OptionsStep";
import ReviewStep from "./wizard/ReviewStep";
import { esignApi, esignTemplatesApi, mattersApi } from "../../services/api";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  preSelectedMatterId?: string;
}

export interface WizardData {
  // Step 1
  matterId: string;
  templateId: string;
  subject: string;
  message: string;
  documents: { documentId: string; title: string; requiresSignature: boolean }[];

  // Step 2
  signers: {
    name: string;
    email: string;
    role: string;
    order: number;
    clientId?: string;
    color: string;
    createAsClient?: boolean;
  }[];

  // Step 3
  fields: {
    documentIndex: number;
    signerIndex: number;
    type: "signature" | "initials" | "date_signed" | "text" | "checkbox";
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    required: boolean;
    label: string;
  }[];

  // Step 4
  sequentialSigning: boolean;
  expirationDays: number;
  reminders: { enabled: boolean; intervalDays: number; maxReminders: number };
  saveAsTemplate: boolean;
  templateName: string;

  // Sender field values (filled by creator on review step)
  senderFieldValues?: Record<number, string>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

const SIGNER_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

const STEPS = [
  { label: "Documents", icon: FileText },
  { label: "Signers", icon: Users },
  { label: "Place Fields", icon: MousePointer },
  { label: "Options", icon: Settings },
  { label: "Review", icon: CheckCircle },
];

const INITIAL_DATA: WizardData = {
  matterId: "",
  templateId: "",
  subject: "",
  message: "",
  documents: [{ documentId: "", title: "", requiresSignature: true }],
  signers: [{ name: "", email: "", role: "client", order: 1, color: SIGNER_COLORS[0] }],
  fields: [],
  sequentialSigning: false,
  expirationDays: 30,
  reminders: { enabled: true, intervalDays: 3, maxReminders: 5 },
  saveAsTemplate: false,
  templateName: "",
};

// ─── Component ────────────────────────────────────────────────────────────────

const CreateEnvelopeWizard: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  preSelectedMatterId,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<WizardData>(() => ({
    ...INITIAL_DATA,
    matterId: preSelectedMatterId || "",
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [direction, setDirection] = useState(1);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);

  // Reset when modal opens
  React.useEffect(() => {
    if (open) {
      setData({
        ...INITIAL_DATA,
        matterId: preSelectedMatterId || "",
      });
      setCurrentStep(0);
      setErrors({});
      setDirection(1);
      setSubmitting(false);
    }
  }, [open, preSelectedMatterId]);

  const handleChange = useCallback(
    (updated: WizardData) => {
      setData(updated);
      // Clear errors
      setErrors({});
    },
    []
  );

  // Check if user has entered any data worth preserving
  const isDirty = useMemo(() => {
    if (data.subject.trim()) return true;
    if (data.message.trim()) return true;
    if (data.documents.some((d) => d.documentId)) return true;
    if (data.signers.some((s) => s.name.trim() || s.email.trim())) return true;
    if (data.fields.length > 0) return true;
    if (data.matterId) return true;
    if (data.templateId) return true;
    return false;
  }, [data]);

  // Auto-fill first signer when matter is selected (if it has a client)
  const prevMatterId = useRef(data.matterId);
  useEffect(() => {
    if (data.matterId && data.matterId !== prevMatterId.current) {
      mattersApi.get(data.matterId).then((res) => {
        const matter = res.data?.data || res.data;
        if (matter?.clientId) {
          const client = typeof matter.clientId === "object" ? matter.clientId : null;
          if (client) {
            const clientName = client.displayName || client.name || "";
            const clientEmail =
              client.emails?.find((e: any) => e.label === "main")?.email ||
              client.emails?.[0]?.email ||
              client.email || "";
            // Only auto-fill if first signer is still empty
            setData((prev) => {
              const firstSigner = prev.signers[0];
              if (firstSigner && !firstSigner.name && !firstSigner.email) {
                const updated = [...prev.signers];
                updated[0] = {
                  ...updated[0],
                  name: clientName,
                  email: clientEmail,
                  clientId: client._id,
                  role: "client",
                };
                return { ...prev, signers: updated };
              }
              return prev;
            });
          }
        }
      }).catch(() => {});
    }
    prevMatterId.current = data.matterId;
  }, [data.matterId]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardWarning(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!data.subject.trim()) {
        newErrors.subject = "Subject is required";
      }
      const validDocs = data.documents.filter((d) => d.documentId);
      if (validDocs.length === 0) {
        newErrors.documents = "At least one document is required";
      }
    }

    if (step === 1) {
      const validSigners = data.signers.filter(
        (s) => s.name.trim() && s.email.trim()
      );
      if (validSigners.length === 0) {
        newErrors.signers = "At least one signer with name and email is required";
      }
      // Email validation
      for (const signer of data.signers) {
        if (signer.email && !signer.email.includes("@")) {
          newErrors.signers = `Invalid email for ${signer.name || "a signer"}`;
          break;
        }
      }
    }

    // Step 2 (Place Fields) is optional - no validation
    // Step 3 (Options) - validate template name if saving
    if (step === 3) {
      if (data.saveAsTemplate && !data.templateName.trim()) {
        newErrors.templateName = "Template name is required when saving as template";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goNext = () => {
    if (!validateStep(currentStep)) return;
    setDirection(1);
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setDirection(-1);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const buildPayload = () => {
    const validDocs = data.documents.filter((d) => d.documentId);
    const validSigners = data.signers.filter(
      (s) => s.name.trim() && s.email.trim()
    );

    return {
      matterId: data.matterId || undefined,
      subject: data.subject.trim(),
      message: data.message.trim() || null,
      documents: validDocs.map((d) => ({
        documentId: d.documentId,
        title: d.title,
        requiresSignature: d.requiresSignature,
      })),
      signers: validSigners.map((s) => ({
        name: s.name.trim(),
        email: s.email.trim(),
        role: s.role,
        order: s.order,
        clientId: s.clientId || undefined,
        createAsClient: s.createAsClient || undefined,
      })),
      fields: data.fields.map((f, i) => {
        const senderVal = data.senderFieldValues?.[i];
        if (f.signerIndex === -1 && senderVal) {
          return { ...f, value: senderVal, completedAt: new Date().toISOString() };
        }
        return f;
      }),
      sequentialSigning: data.sequentialSigning,
      expirationDays: data.expirationDays,
      reminders: data.reminders,
    };
  };

  const handleSaveDraft = async () => {
    setSubmitting(true);
    setErrors({});

    try {
      const payload = buildPayload();
      await esignApi.create({ ...payload, status: "draft" });

      // Save as template if needed
      if (data.saveAsTemplate && data.templateName.trim()) {
        try {
          await esignTemplatesApi.create({
            name: data.templateName.trim(),
            defaults: {
              subject: data.subject,
              message: data.message,
              sequentialSigning: data.sequentialSigning,
              expirationDays: data.expirationDays,
              remindersEnabled: data.reminders.enabled,
              reminderIntervalDays: data.reminders.intervalDays,
            },
            defaultSigners: data.signers.map((s) => ({
              role: s.role,
              label: s.name,
              order: s.order,
            })),
          });
        } catch (templateErr) {
          console.error("Envelope saved but template creation failed:", templateErr);
        }
      }

      onCreated();
      onClose();
    } catch (err: any) {
      setErrors({
        _form: err.response?.data?.error || "Failed to save draft",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSend = async () => {
    setSubmitting(true);
    setErrors({});

    try {
      const payload = buildPayload();
      const res = await esignApi.create(payload);
      const envelope = res.data?.data || res.data;

      // Send for signing
      if (envelope?._id) {
        try {
          await esignApi.send(envelope._id);
        } catch (sendErr: any) {
          console.error("Envelope created but failed to send:", sendErr);
        }
      }

      // Save as template if needed
      if (data.saveAsTemplate && data.templateName.trim()) {
        try {
          await esignTemplatesApi.create({
            name: data.templateName.trim(),
            defaults: {
              subject: data.subject,
              message: data.message,
              sequentialSigning: data.sequentialSigning,
              expirationDays: data.expirationDays,
              remindersEnabled: data.reminders.enabled,
              reminderIntervalDays: data.reminders.intervalDays,
            },
            defaultSigners: data.signers.map((s) => ({
              role: s.role,
              label: s.name,
              order: s.order,
            })),
          });
        } catch (templateErr) {
          console.error("Envelope sent but template creation failed:", templateErr);
        }
      }

      onCreated();
      onClose();
    } catch (err: any) {
      setErrors({
        _form: err.response?.data?.error || "Failed to create signing request",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const isLastStep = currentStep === STEPS.length - 1;

  // Check if all required sender fields are filled
  const senderFieldsComplete = useMemo(() => {
    const senderFields = data.fields
      .map((f, idx) => ({ ...f, fieldIndex: idx }))
      .filter((f) => f.signerIndex === -1 && f.required);
    if (senderFields.length === 0) return true;
    return senderFields.every((f) => {
      const val = data.senderFieldValues?.[f.fieldIndex];
      return val && val.trim() !== "";
    });
  }, [data.fields, data.senderFieldValues]);

  return (
    <PremiumModal
      open={open}
      onClose={handleClose}
      title="Create Signing Request"
      subtitle={`Step ${currentStep + 1} of ${STEPS.length}: ${STEPS[currentStep]?.label}`}
      icon={PenTool}
      size="xl"
      footer={
        <div className="space-y-2">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={goBack}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
            {isLastStep ? (
              <>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save as Draft
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={submitting || !senderFieldsComplete}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
                  style={{ backgroundColor: PRIMARY }}
                  onMouseEnter={(e) =>
                    !(submitting || !senderFieldsComplete) && (e.currentTarget.style.backgroundColor = HOVER)
                  }
                  onMouseLeave={(e) =>
                    !(submitting || !senderFieldsComplete) && (e.currentTarget.style.backgroundColor = PRIMARY)
                  }
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send for Signing
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors"
                style={{ backgroundColor: PRIMARY }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = HOVER)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = PRIMARY)
                }
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        {isLastStep && !senderFieldsComplete && !submitting && (
          <div className="flex items-center justify-end gap-1.5 text-xs text-amber-600">
            <AlertCircle className="h-3.5 w-3.5" />
            Please fill in all required sender fields above before sending
          </div>
        )}
        </div>
      }
    >
      {/* Step Indicator */}
      <div className="mb-6">
        <WizardStepIndicator currentStep={currentStep} steps={STEPS} />
      </div>

      {/* Form Error */}
      {errors._form && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          {errors._form}
        </div>
      )}

      {/* Validation Errors */}
      {Object.keys(errors).filter((k) => k !== "_form").length > 0 && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <ul className="list-disc list-inside space-y-1">
            {Object.entries(errors)
              .filter(([k]) => k !== "_form")
              .map(([key, msg]) => (
                <li key={key}>{msg}</li>
              ))}
          </ul>
        </div>
      )}

      {/* Step Content with animation */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: direction > 0 ? 30 : -30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction > 0 ? -30 : 30 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          {currentStep === 0 && (
            <DocumentsStep data={data} onChange={handleChange} />
          )}
          {currentStep === 1 && (
            <SignersStep data={data} onChange={handleChange} />
          )}
          {currentStep === 2 && (
            <PlaceFieldsStep
              data={data}
              onChange={(partial) => setData((prev) => ({ ...prev, ...partial }))}
            />
          )}
          {currentStep === 3 && (
            <OptionsStep data={data} onChange={handleChange} />
          )}
          {currentStep === 4 && <ReviewStep data={data} onChange={handleChange} />}
        </motion.div>
      </AnimatePresence>

      {/* Discard draft warning */}
      {showDiscardWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 max-w-sm mx-4"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-amber-500" />
              </div>
              <h3 className="text-base font-bold text-slate-800">Discard draft?</h3>
            </div>
            <p className="text-sm text-slate-500 mb-5">
              You have unsaved changes. If you close now, your signing request draft will be lost.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDiscardWarning(false)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={() => {
                  setShowDiscardWarning(false);
                  onClose();
                }}
                className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors"
              >
                Discard
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </PremiumModal>
  );
};

export default CreateEnvelopeWizard;
